import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Tooltip,
  styled,
  tableCellClasses,
  Pagination,
  MenuItem,
  Switch,
  Divider,
} from '@mui/material';
import { ReactComponent as EditIcon } from '../../assets/svg/edit.svg';
import { ReactComponent as Trash } from '../../assets/svg/trash.svg';
import { ReactComponent as Approval } from '../../assets/svg/approval.svg';
import CommonDrawer from '../../CommonComponents/CommonDrawer';
import CommonConfirmationModal from '../../CommonComponents/CommonConfirmationModal';
import { showErrorToast, showSuccessToast } from '../../toastUtils';
import { useUser } from '../../components/UserContext';
import ChangeRequestViewModal from '../../CommonComponents/ChangeRequestViewModal';
import { getApprovalById } from '../../api/approval';
import { createCategory, deleteCategory, getAllCategory, updateCategory } from '../../api/category';
import CommonHeader from '../../CommonComponents/CommonHeader';
import SearchInput from '../../CommonComponents/SearchInput';
import ButtonCommon from '../../CommonComponents/ButtonCommon';
import { Add } from '@mui/icons-material';
import SelectCommon from '../../CommonComponents/SelectCommon';
import ComponentWrapper from '../../CommonComponents/ComponentWrapper';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F9FAFB",
    color: "#000000",
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const CategoriesPage = () => {
  const { currentUser } = useUser();
  // const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryType: ''
  });
  const [currentCategory, setCurrentCategory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [paginatedData, setPaginatedData] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [statusValue, setStatusValue] = useState('all');
  const [typeValue, setTypeValue] = useState('all');
  const [filteredData, setFilteredData] = useState([]);
  const [viewCategory, setViewCategory] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [approvalLoader, setApprovalLoader] = useState(false);
  const [showActivateSwitch, setShowActivateSwitch] = useState(false);

  useEffect(() => {
    if (currentCategory) {
      setFormData({
        name: currentCategory.category_name || '',
        description: currentCategory.description || '',
        categoryType: currentCategory.category_type || '',
        isActive: currentCategory.is_deleted ? false : true
      });
    } else {
      resetForm();
    }
  }, [currentCategory]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      categoryType: '',
    });
    setErrors({});
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getAllCategory(page, perPage);
      if (response.success) {
        const data = response.data.categories || [];
        setPaginatedData(data);
        setFilteredData(data || []);
        setTotalCount(response.data?.total_categories || 0);
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      showErrorToast(error.message)
      setPaginatedData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();

    // eslint-disable-next-line
  }, [page, perPage]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Category name is required";
    }

    if (!formData.description?.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.categoryType?.trim()) {
      newErrors.categoryType = "Category type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCategory = () => {
    setCurrentCategory(null);
    setOpenDrawer(true);
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    setOpenDrawer(true);
    setShowActivateSwitch(category.status === 'inactive' && true)
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setCurrentCategory(null);
    setIsEdited(false)
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === 'isActive' ? checked : value,
    }));

    // Clear error for the field being edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    setIsEdited(true)
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitLoading(true);

      const payload = {
        category_name: formData.name,
        description: formData.description,
        category_type: formData.categoryType,
        is_deleted: !formData.isActive,
      };

      let response;
      if (currentCategory?.category_id) {

        if ('is_deleted' in payload && payload.is_deleted) {
          delete payload.is_deleted;
        }

        // Update existing language
        response = await updateCategory(currentCategory.category_id, payload);
      } else {
        if ('is_deleted' in payload) {
          delete payload.is_deleted;
        }

        // Create new language
        response = await createCategory(payload);
      }

      if (response?.success) {
        showSuccessToast(response.message);
        handleCloseDrawer();
        fetchCategories();
      } else {
        showErrorToast(response.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error submitting category:', error);
      showErrorToast(error.message);
    } finally {
      setSubmitLoading(false);
      setIsEdited(currentCategory ? false : true)
      setShowActivateSwitch(false)
    }
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete?.category_id) {
      showErrorToast('Invalid category selected for deletion.');
      return;
    }

    try {
      setDeleteLoading(true);

      const response = await deleteCategory(categoryToDelete.category_id);

      if (response.success) {
        showSuccessToast(response.message);
        fetchCategories();
      } else {
        showErrorToast(response.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error); // 🔥
      showErrorToast(error.message);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  // Add view handler
  const handleViewClick = async (category) => {
    setApprovalLoader(category.approval_id);
    try {
      const response = await getApprovalById(category.approval_id);

      if (response.success) {
        const approvalData = response.data;

        setViewCategory({
          ...approvalData,
          // requested_by: approvalData.requested_by || "Unknown",
          // requested_at: approvalData.requested_at || new Date().toISOString(),
        });

        setViewModalOpen(true);
      } else {
        console.error('No data received for category approval.');
      }
    } catch (error) {
      console.error('Error fetching category approval details:', error);
    } finally {
      setApprovalLoader(null);
    }
  };

  useEffect(() => {
    const data = paginatedData?.length ? paginatedData : [];

    const filtered = data.filter((item) => {
      const search = searchValue?.toLowerCase() || '';
      const category = item.category_name?.toLowerCase() || '';
      const isNameMatch = category.includes(search);

      const isTypeMatch = typeValue === 'all'
        ? true
        : item.category_type?.toLowerCase() === typeValue?.toLowerCase();

      const isStatusMatch = statusValue === 'all'
        ? true
        : item.status?.toLowerCase() === statusValue?.toLowerCase();

      return isNameMatch && isStatusMatch && isTypeMatch;
    });

    setFilteredData(filtered);
  }, [searchValue, statusValue, typeValue, paginatedData]);

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography sx={{ color: "#525455", mb: 1, fontSize: "0.875rem" }}>
          Category Name
          <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
        </Typography>
        <TextField
          fullWidth
          name="name"
          size='small'
          value={formData.name}
          onChange={handleChange}
          disabled={currentCategory && !formData.isActive}
          error={!!errors.name}
          helperText={errors.name}
          margin="normal"
          sx={{ m: 0 }}
        />
      </Box>
      <Box>
        <Typography sx={{ color: "#525455", mb: 1, fontSize: "0.875rem" }}>
          Description
          <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
        </Typography>
        <TextField
          fullWidth
          size='small'
          name="description"
          value={formData.description}
          onChange={handleChange}
          disabled={currentCategory && !formData.isActive}
          error={!!errors.description}
          helperText={errors.description}
          margin="normal"
          sx={{ m: 0 }}
        />
      </Box>
      <Box>
        <Typography sx={{ color: "#525455", mb: 1, fontSize: "0.875rem" }}>
          Category Type
          <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          name="categoryType"
          value={formData.categoryType}
          onChange={handleChange}
          disabled={currentCategory && !formData.isActive}
          error={Boolean(errors.categoryType)}
          helperText={errors.categoryType}
          sx={{ m: 0 }}
        >
          <MenuItem value="Required">Required</MenuItem>
          <MenuItem value="Optional">Optional</MenuItem>
        </TextField>
      </Box>

      {currentCategory && showActivateSwitch && <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Divider sx={{ mb: 1 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "#525455" }}
          >
            Activate Category
            {/* <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box> */}
          </Typography>
          <Switch name="isActive" checked={formData.isActive} onChange={handleChange} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          If disabled, this category will not be shown to any visitors.
        </Typography>
      </Box>}
    </Box>
  );

  return (
    <ComponentWrapper>
      {/* <Stack sx={{ maxWidth: '1000px' }}> */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', mb: 3 }}>
        <CommonHeader
          title="Master Category"
          subTitle="You can review, audit and change the status of master category on this page."
        />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'end', flexDirection: 'row', mb: 2 }}>
          <SearchInput
            placeholder="Search by Category Name"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <SelectCommon
            label="Type"
            value={typeValue}
            onChange={(e) => setTypeValue(e.target.value)}
            options={[
              { label: "All", value: "all" },
              { label: "Optional", value: "optional" },
              { label: "Required", value: "required" },
            ]}
          />
          <SelectCommon
            label="Status"
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
              { label: "Draft", value: "draft" },
            ]}
          />
          <ButtonCommon icon={<Add />} onClick={handleAddCategory}>
            Add Category
          </ButtonCommon>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "12px" }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Category Name</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="center">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredData && filteredData?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No data requests found.
                </TableCell>
              </TableRow>
            ) : (
              filteredData?.map((row) => {
                return (
                  <TableRow key={row.category_id} hover>
                    <StyledTableCell>{row.category_name}</StyledTableCell>
                    <StyledTableCell>{row.category_type}</StyledTableCell>
                    <StyledTableCell>
                      {row.description.length > 60
                        ? `${row.description.slice(0, 60)}...`
                        : row.description}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        textTransform: "capitalize",
                        color:
                          row.status === "active"
                            ? "#00B894"
                            : row.status === "inactive"
                              ? "#FF946D"
                              : "#6994FF",
                        fontWeight: 600,
                      }}
                    >{row.status}</StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: '140px' }}>
                      {currentUser?.zoneinfo === "super_admin" && <Tooltip title="Approval" arrow>
                        <IconButton
                          size="small"
                          disabled={
                            row.status !== "draft" ||
                            currentUser?.zoneinfo === "product_user"
                          }
                          sx={{
                            color: (theme) =>
                              row.status !== "draft" ||
                                currentUser?.zoneinfo === "product_user"
                                ? theme.palette.grey[400]
                                : "inherit",
                          }}
                          onClick={() => handleViewClick(row)}
                        >
                          {/* {approvalLoader ? <CircularProgress size={20} /> : <Eye />} */}
                          {(row.approval_id !== null && approvalLoader === row.approval_id) ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Approval />
                          )}
                        </IconButton>
                      </Tooltip>}
                      {console.log(' row.approval_id', typeof row.approval_id === 'number')}

                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleEditCategory(row)}
                          disabled={row.status === "draft" || row.approval_id}
                          sx={{
                            color: (theme) =>
                              row.status === "draft"
                                ? theme.palette.grey[400]
                                : "inherit",
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(row)}
                          disabled={row.status === "draft" || row.status === "inactive" || row.approval_id}
                          sx={{
                            color: (theme) =>
                              row.status === "draft"
                                ? theme.palette.grey[400]
                                : "inherit",
                          }}
                        >
                          <Trash />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(totalCount / perPage)}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          shape="rounded"
          variant="outlined"
        />
      </Box>

      {/* Use CommonDrawer instead of custom LanguageDrawer */}
      <CommonDrawer
        open={openDrawer}
        onClose={handleCloseDrawer}
        title={currentCategory ? "Edit Category" : "Add New Category"}
        onSubmit={handleSubmit}
        primaryButton={{
          label: submitLoading ?
            <CircularProgress size={24} color="inherit" /> :
            (currentCategory ? "Update" : "Create"),
          disabled: submitLoading || !isEdited || (currentCategory && !formData.isActive)
        }}
        secondaryButton={{
          disabled: submitLoading
        }}
        drawerWidth={500}
      >
        {drawerContent}
      </CommonDrawer>

      <CommonConfirmationModal
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        description={`Are you sure you want to delete the category?`}
        loading={deleteLoading}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <ChangeRequestViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fetchData={fetchCategories}
        title={`Category ${viewCategory?.action_type} request`}
        approvalId={viewCategory?.approval_id}
        beforeData={viewCategory?.data_before ? {
          name: viewCategory?.data_before?.category_name,
          description: viewCategory?.data_before?.description,
          type: viewCategory?.data_before?.category_type,
          status: viewCategory?.data_before?.is_deleted ? 'Inactive' : 'Active'
        } : null}
        afterData={{
          name: viewCategory?.data_after?.category_name,
          description: viewCategory?.data_after?.description,
          type: viewCategory?.data_after?.category_type,
          status: viewCategory?.data_after?.is_deleted ? 'Inactive' : 'Active'
        }}
        requestedBy={viewCategory?.requested_by}
        requestedAt={viewCategory?.requested_at}
        actionType={viewCategory?.action_type}
      // onApprove={() => handleApproveLanguage(viewCategory)}
      // onReject={() => handleRejectLanguage(viewCategory)}
      />
      {/* </Stack> */}
    </ComponentWrapper >
  );
};

export default CategoriesPage;
